import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import { Calendar } from 'react-date-range';
import { isWithinInterval, isEqual } from 'date-fns';
import theme from 'theme';
import PropTypes from 'prop-types';
import Button from '../Button';

const DISPLAY_DATE_FORMAT = 'MM/dd/yy';

const CalendarPicker = ({
  classes,
  initialDate,
  dateFormat,
  buttonLabel,
  onDateChange,
  onUpdateClicked,
  disabledDates,
  disableDayPredicate,
  minDate,
  maxDate,
  withValidators,
}) => {
  const [date, setDate] = React.useState(initialDate);

  const handleOnChange = (date) => {
    setDate(date);
    onDateChange(date);
  };

  const handleOnUpdate = () => {
    onUpdateClicked(date);
  };

  const dateRangeLimitProps = {
    ...(minDate && { minDate }),
    ...(maxDate && { maxDate }),
  };

  const isDateValid = () => {
    if (!withValidators) {
      return !!date;
    }

    const dateValidatorResults = [];

    if (!_isEmpty(dateRangeLimitProps)) {
      const isInRange = isWithinInterval(date, {
        start: dateRangeLimitProps.minDate,
        end: dateRangeLimitProps.maxDate,
      });

      dateValidatorResults.push(isInRange);
    }

    const isDateDisabledByPredicate = disableDayPredicate(date);
    dateValidatorResults.push(!isDateDisabledByPredicate);

    const isDateDisabledDueToRestrictions = disabledDates.some((disabledDay) => isEqual(date, disabledDay));
    dateValidatorResults.push(!isDateDisabledDueToRestrictions);

    return dateValidatorResults.every((validatorResult) => validatorResult);
  };

  return (
    <>
      <div className={classes.calendarWrapper}>
        <Calendar
          color={theme.color.black}
          onChange={handleOnChange}
          className={classes.calendar}
          date={date}
          dateDisplayFormat={dateFormat}
          disabledDates={disabledDates}
          disabledDay={disableDayPredicate}
          {...dateRangeLimitProps}
        />
      </div>
      <div className={classes.footer}>
        <Button
          disabled={!isDateValid()}
          data-testid={buttonLabel}
          label={buttonLabel}
          className={classes.calendarButton}
          onClick={handleOnUpdate}
        />
      </div>
    </>
  );
};

CalendarPicker.propTypes = {
  buttonLabel: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string),
  dateFormat: PropTypes.string,
  initialDate: PropTypes.instanceOf(Date),
  onDateChange: PropTypes.func,
  onUpdateClicked: PropTypes.func.isRequired,
  disableDayPredicate: PropTypes.func,
  disabledDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  withValidators: PropTypes.bool,
};

CalendarPicker.defaultProps = {
  buttonLabel: 'update',
  classes: {},
  dateFormat: DISPLAY_DATE_FORMAT,
  initialDate: null,
  onDateChange: () => {},
  disableDayPredicate: () => {},
  disabledDates: [],
};

export default CalendarPicker;
